import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import type { LineupPlanId } from '@highlight/common'
import { planToken } from './plan-select-cards'

const GLYPH_INK: Record<ReturnType<typeof planToken>, string> = {
  brand: 'text-brand',
  'premium-green': 'text-success',
  'premium-blue': 'text-info',
}

interface PlanNoteProps {
  icon: ReactNode
  /** The plan the note sells or refills, which inks the glyph. Pro's green when absent. */
  plan?: LineupPlanId
  title: ReactNode
  body: ReactNode
  actions?: ReactNode
  className?: string
}

/**
 * A note about the plan where the person is looking, such as in a chat or at a
 * meeting's end: a glyph, the fact, a line, and the ways on.
 */
export function PlanNote({ icon, plan = 'pro2', title, body, actions, className }: PlanNoteProps) {
  return (
    <div
      role="note"
      className={twMerge('bg-background-subtle corner-smooth flex gap-3 rounded-xl border p-3', className)}
    >
      <span
        aria-hidden="true"
        className={twMerge(
          'bg-background corner-smooth flex size-7 shrink-0 items-center justify-center rounded-lg border [&_svg]:size-4',
          GLYPH_INK[planToken(plan)],
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="type-ui-sm text-foreground m-0 text-pretty font-medium">{title}</p>
        <p className="type-ui-sm text-foreground-secondary m-0 text-pretty">{body}</p>
        {actions && <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  )
}
